import { useState, useEffect } from 'react';

/**
 * Hook to defer non-critical work until the browser is idle.
 * Useful for deferring heavy widgets on mobile to improve LCP.
 * 
 * @param timeout - Fallback timeout in ms if requestIdleCallback is not supported (default: 2000)
 * @param mobileOnly - Only defer on mobile devices (default: true)
 * @returns boolean indicating if the idle callback has fired
 */
export function useIdleCallback(timeout = 2000, mobileOnly = true): boolean {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // On server or during SSR, don't run
    if (typeof window === 'undefined') return;

    const isMobile = window.innerWidth < 768;
    
    // If mobileOnly is true and we're on desktop, set ready immediately
    if (mobileOnly && !isMobile) {
      setIsReady(true);
      return;
    }

    // Use requestIdleCallback if available, otherwise fall back to setTimeout
    if ('requestIdleCallback' in window && typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(
        () => setIsReady(true),
        { timeout }
      );
      
      return () => {
        window.cancelIdleCallback(id);
      };
    } else {
      const id = window.setTimeout(() => setIsReady(true), timeout);
      return () => window.clearTimeout(id);
    }
  }, [timeout, mobileOnly]);

  return isReady;
}

export default useIdleCallback;
